import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Options } from '@contentful/rich-text-react-renderer';
import type { Document } from '@contentful/rich-text-types';
import { BLOCKS } from '@contentful/rich-text-types';
import { useQuery } from '@tanstack/react-query';
import type {
  BlogNavigationProps,
  SideRailArticlesProps,
  ImageType,
} from '@we-make-websites/ui-lib';
import { useRouter } from 'next/router';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { createContext } from 'use-context-selector';
import { storeLocale } from '@/root/constants';
import { ContentSectionComponent } from 'components/contentSectionComponent/ContentSectionComponent';
import { ProductRecommendations } from 'components/productRecommendations/ProductRecommendationsWrapper';
import { useGetBlogArticleQuery } from 'lib/contentful/__generated__/BlogArticleQuery';
import type { GetBlogArticleQuery } from 'lib/contentful/__generated__/BlogArticleQuery';
import type { ArticleContentLinks } from 'lib/contentful/__generated__/types';
import { contentfulQueryDataSource } from 'lib/contentful/dataSources';
import { getContentfulTagFilter } from 'utils/contentfulHelpers';
import { formatContentfulSections } from 'utils/format/contentful/formatContentfulSections';
import { formatModuleBlogNavigation } from 'utils/format/contentful/formatModuleBlogNavigation';
import { formatModuleSideRailArticles } from 'utils/format/contentful/formatModuleSideRailArticles';
import { usePreviewCookie } from 'utils/getPreviewCookie';

import type {
  FormattedContentfulSection,
  InputContentfulSection,
  InputContentfulSections,
} from 'utils/types/contentfulSections';
import type { StoreSeo } from 'utils/types/storeTypes';

type BlogArticleData = {
  categoryRef?: {
    category: string;
    handle: string;
  };
  title: string;
  image?: ImageType;
  seo?: StoreSeo;
  content?: ReactNode;
  sectionCount?: number;
  sideRailArticles?: SideRailArticlesProps['articles'];
  articlesListing?: FormattedContentfulSection[];
  navigation?: BlogNavigationProps & { heading?: string };
};

export type BlogArticleContext = BlogArticleData & {
  isLoading: boolean;
};

export const BlogArticleContext = createContext<BlogArticleContext | undefined>(
  undefined
);

export function BlogArticleProvider({
  handle,
  children,
}: {
  handle: string;
  children: ReactNode;
}) {
  const { locale: routerLocale } = useRouter();

  const locale = storeLocale(routerLocale);

  const preview = usePreviewCookie();

  const variables = {
    handle,
    locale,
    preview,
    tagFilter: getContentfulTagFilter(locale),
  };

  const { data, isLoading } = useQuery(
    useGetBlogArticleQuery.getKey(variables),
    useGetBlogArticleQuery.fetcher(
      contentfulQueryDataSource({ byPassCache: preview }),
      variables
    ),
    {
      select: getBlogArticleSelect,
    }
  );

  const contextValue = useMemo(() => {
    if (data) {
      return {
        ...data,
        isLoading,
      };
    }

    return {
      isLoading,
      title: '',
    };
  }, [data, isLoading]);

  return (
    <BlogArticleContext.Provider value={contextValue}>
      {children}
    </BlogArticleContext.Provider>
  );
}

const getBlogArticleSelect = (
  originalData: GetBlogArticleQuery
): BlogArticleData | null => {
  if (!originalData.article?.data[0]) {
    return null;
  }

  const article = originalData.article?.data[0];

  const navigation = article.navigation
    ? formatModuleBlogNavigation(article.navigation)
    : undefined;

  const sideRailArticles = article.sideRailArticles
    ? formatModuleSideRailArticles(article.sideRailArticles)
    : undefined;

  const content = documentToReactComponents(
    article.content?.json as Document,
    renderOptions(
      article.content?.links as ArticleContentLinks,
      !!sideRailArticles
    )
  );

  const articlesListingData = [
    article.articleListing,
  ] as InputContentfulSections;
  const articlesListing = article.articleListing
    ? formatContentfulSections(articlesListingData)
    : undefined;

  const seo = {
    title: article.seoTitle || '',
    description: article.seoDescription || '',
  };

  const title = article.title ?? '';
  const categoryRef = {
    category: article.categoryRef?.category ?? '',
    handle: article.categoryRef?.handle ?? '',
  };
  const image = {
    height: article.image?.height ?? 0,
    width: article.image?.width ?? 0,
    url: article.image?.url ?? '',
    title: article.image?.title ?? '',
  };

  return {
    categoryRef,
    title,
    image,
    seo,
    content,
    sideRailArticles,
    articlesListing,
    navigation,
  };
};

function renderOptions(
  links: ArticleContentLinks | undefined,
  sideRailExists: boolean
): Options | undefined {
  if (!links) return;
  // create an entry map
  const entryMap = new Map<string, InputContentfulSection>();
  // loop through the block linked entries and add them to the map
  for (const entry of links.entries.block) {
    if (!entry?.sys.id) continue;

    entryMap.set(entry?.sys.id, entry as InputContentfulSection);
  }

  return {
    // other options...
    renderNode: {
      // other options...
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        // find the entry in the entryMap by ID
        const entry = entryMap.get(node.data.target.sys.id);

        if (!entry) return null;

        const [section] = formatContentfulSections([entry]);

        if (!section) return null;

        // render the entries as needed by looking at the __typename
        // referenced in the GraphQL query
        if (section.__typename === 'ManualProductRecommendations') {
          return (
            <div className={!sideRailExists ? 'container' : ''}>
              <ProductRecommendations {...section} placement="blog" />
            </div>
          );
        }

        return <ContentSectionComponent section={section} />;
      },
    },

    /**
     * Refines rich text from contentful and adds new line
     *
     * @param text - Rich text on contentful
     * @returns string of text with new lines
     */
    renderText: (text) =>
      text.split('\n').flatMap((text, i) => [i > 0 && <br />, text]),
  };
}
